import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { AdBannerService } from 'src/app/_services/ad-banner.service';
import { BANNER_GROUPS } from 'src/app/_models/ad-banner';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-ad-banner-form',
    templateUrl: './ad-banner-form.component.html',
    styleUrls: ['./ad-banner-form.component.css'],
    standalone: false
})
export class AdBannerFormComponent implements OnInit {

    form: UntypedFormGroup;
    bannerGroups = BANNER_GROUPS;
    loading = false;
    submitting = false;
    isEdit = false;
    bannerId: string | null = null;
    selectedFile: File | null = null;
    filePreview: string | ArrayBuffer | null = null;
    currentImagePath: string | null = null;

    constructor(
        private fb: UntypedFormBuilder,
        private adBannerService: AdBannerService,
        private route: ActivatedRoute,
        private router: Router,
        private toastr: ToastrService
    ) {
        this.form = this.fb.group({
            bannerGroup: ['', Validators.required],
            position: [1, [Validators.required, Validators.min(1)]],
            url: [''],
            active: [true]
        });
    }

    ngOnInit(): void {
        this.bannerId = this.route.snapshot.paramMap.get('id');
        if (this.bannerId) {
            this.isEdit = true;
            this.loadBanner();
        }
    }

    loadBanner(): void {
        if (!this.bannerId) return;
        this.loading = true;
        this.adBannerService.loadById(this.bannerId).subscribe({
            next: (banner) => {
                this.form.patchValue({
                    bannerGroup: banner.bannerGroup,
                    position: banner.position,
                    url: banner.url || '',
                    active: banner.active
                });
                this.currentImagePath = environment.image_cdn_url + '/' + banner.imagePath;
            },
            error: () => this.toastr.error('Failed to load ad banner'),
            complete: () => this.loading = false
        });
    }

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            this.selectedFile = input.files[0];
            const reader = new FileReader();
            reader.onload = () => { this.filePreview = reader.result; };
            reader.readAsDataURL(this.selectedFile);
        }
    }

    clearFile(): void {
        this.selectedFile = null;
        this.filePreview = null;
    }

    submit(): void {
        if (this.form.invalid) {
            Object.values(this.form.controls).forEach(c => c.markAsTouched());
            return;
        }
        if (!this.isEdit && !this.selectedFile) {
            this.toastr.error('Please select an image file');
            return;
        }

        this.submitting = true;
        const formData = {
            bannerGroup: this.form.value.bannerGroup,
            position: this.form.value.position,
            url: this.form.value.url || ''
        };

        if (this.isEdit && this.bannerId) {
            this.adBannerService.update(this.bannerId, this.selectedFile, formData).subscribe({
                next: () => {
                    this.toastr.success('Ad banner updated successfully');
                    this.router.navigate(['/admin', 'ad-banners']);
                },
                error: () => {
                    this.toastr.error('Failed to update ad banner');
                    this.submitting = false;
                }
            });
        } else {
            this.adBannerService.create(this.selectedFile!, formData).subscribe({
                next: () => {
                    this.toastr.success('Ad banner created successfully');
                    this.router.navigate(['/admin', 'ad-banners']);
                },
                error: () => {
                    this.toastr.error('Failed to create ad banner');
                    this.submitting = false;
                }
            });
        }
    }

    cancel(): void {
        this.router.navigate(['/admin', 'ad-banners']);
    }
}
